// LAYOUT-SPEC §0 — a team (wireframe 3), and the contract-wide group
//
//   ONE QUESTION  What does this team owe, how does its SOW say to carry each
//                 deliverable out, and what is due or wrong right now?
//   PRIMARY OBJECT  One workstream (Archetype A, record cockpit with a strip).
//   PRIMARY VERB    + Submit. Handling a deliverable, adding a SOW and linking
//                 an exercise line are the setup verbs.
//   WORST-CASE VOLUME  ~40 deliverables across two or three SOW versions.
//
// A team is only a grouping: its deliverables come from award rules; its SOWs
// say how each is handled here (performed, routed, not owed). The strip is the
// team's own current window (D104). /workstreams/contract-wide is the same
// shell for the dates no team owes, "Contract-wide (<period>)" (D109).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ExecutionPerformanceDisposition } from '@mono/proto-clients';
import { SpecraBreadcrumbs, SpecraButton, SpecraDashboardPage, SpecraDateField, SpecraEmptyState, SpecraFilterToolbar, SpecraFormField, SpecraHeading, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraPageHeader, SpecraRecordGroups, SpecraSectionHeader, SpecraSelect, SpecraTabBar, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { ErrorState } from '../../_shared/components/PageStates';
import { useProgramDocuments } from '../../Docs/hooks';
import { UploadSowButton } from '../../Docs/UploadSowButton';
import { GlossaryText } from '../../Glossary/GlossaryText';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { CmAwaitingApproval, CmPendingSubmission, CmSourceButton, CmStateTable, CmStatusBadge, CmTable, DutySourceChips, routedLink, SowSilentLink, SowStateBadge } from '../components/common';
import { DeliverableFilters } from '../components/DeliverableFilters';
import { LOADING_STRIP, stripProps, useTeamKpi } from '../components/KpiStrip';
import { LogEventDrawer } from '../components/LogEventDrawer';
import { TeamLinesDrawer, TeamLinesTags } from '../components/TeamLines';
import { TeamMembersPanel } from '../components/TeamMembers';
import { WorkProductsPanel } from '../components/WorkProducts';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, SowGapChoice, useCreateSow, useDutiesDetail, useOccurrences, useOccurrencesMany, useRuleSowGap, useSetExerciseWorkstream, useWorkProducts, useWorkstream, useWorkstreamDetails, useWorkstreamMembers } from '../hooks';
import { sourceDocChipLabel } from '../lib/dutySource';
import { cmDate, cmDateShort, cmSpan } from '../lib/format';
import { CONDITION_NOT_SETTLED, conditionHeldStatus, HANDLING_LABEL, isOccurrenceState, isOpenState, NOT_OWED_REASON_LABEL, occurrenceStatus, STATE_SHORT_LABEL, undatedStatus } from '../lib/status';
import { clinFilterOptions, filterDeliverableRows, fromUiSortDir, normalizeTeamId, periodIdsForOccurrences, sortDeliverableRows, toUiSortDir, uniqueSorted } from '../lib/tableControls';
import { useCmLookups, useConditionHolds, useSowSilentQuestions } from '../lookups';
import { useContractWideLabel } from './TeamsPage';
/** R12-C: "shared with N teams" once more than one team sees the series. */ function sharedNote(r) {
    return r.sharedWith && r.sharedWith > 1 ? ` · shared with ${r.sharedWith} teams` : '';
}
/** The date a row points at: the earliest open one, else the latest. */ function nextOf(occs) {
    const open = occs.filter((o)=>isOpenState(o.status?.state ?? '')).sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9'));
    if (open.length) return open[0];
    return [
        ...occs
    ].sort((a, b)=>(b.dueOn || '').localeCompare(a.dueOn || ''))[0];
}
/**
 * SAD-842: a deliverable owed in the window with no firm date in it still
 * reads, in words: held by a condition, projected from an event, or the
 * reason pangaea gives for the rules that govern this row (SAD-967: on
 * request, waiting on an event, needs a start date, no dates this period).
 */ function undatedState(duty, reason, checks, projected, holdOf) {
    if (checks.includes(CONDITION_NOT_SETTLED)) return conditionHeldStatus(holdOf(duty.pwsRef));
    const proj = [
        ...projected
    ].sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9'))[0];
    if (proj) return occurrenceStatus(proj, duty.finalReviewTier);
    return undatedStatus(reason);
}
/** A government request can be logged for the row (an on_request rule, never an as-needed one). */ function isGovernmentRequest(reason) {
    return reason?.reason === 'on_request' && reason.eventKind === 'on_request';
}
/**
 * Rows the team owes itself: performed here, or routed to the base scope,
 * where the team owes its own dated copy of the platform deliverable (M5).
 */ function owedByTeam(r) {
    const a = r.assignment;
    return a?.handling === 'performed' || a?.handling === 'routed' && !a.routedToWorkstreamId;
}
/** Firm dates first by date, then everything undated, each by title. */ function byNextThenTitle(a, b) {
    const ad = a.next?.dueOn ?? '';
    const bd = b.next?.dueOn ?? '';
    if (ad && bd && ad !== bd) return ad.localeCompare(bd);
    if (!ad !== !bd) return ad ? -1 : 1;
    return a.duty.title.localeCompare(b.duty.title);
}
/** Award rules (never a SOW's team rules), the ones the award's gates read. */ function awardRules(rules) {
    return rules.filter((r)=>!r.assignmentId && r.gate !== 'sow');
}
/** Splits a duty's window rows into firm and projected (never an occurrence id). */ function splitProjected(occs) {
    return {
        firm: occs.filter((o)=>!o.event?.projected),
        projected: occs.filter((o)=>o.event?.projected)
    };
}
function byDutyId(occs) {
    const m = new Map();
    for (const o of occs){
        const arr = m.get(o.dutyId) ?? [];
        arr.push(o);
        m.set(o.dutyId, arr);
    }
    return m;
}
function dutyRowFields(duty, next, teamId, occs, periods, exercises) {
    return {
        title: duty.title,
        dutyKey: duty.dutyKey,
        pwsRef: duty.pwsRef,
        letter: duty.letter,
        origin: duty.origin,
        sourceDocumentId: duty.sourceDocumentId,
        teamIds: [
            normalizeTeamId(teamId)
        ],
        periodIds: periodIdsForOccurrences(occs, periods, exercises),
        nextDueOn: next?.dueOn,
        statusState: next?.status?.state
    };
}
export function WorkstreamPage() {
    const { wsId = '' } = useParams();
    return wsId === 'contract-wide' ? /*#__PURE__*/ _jsx(ContractWideView, {}) : /*#__PURE__*/ _jsx(TeamView, {
        wsId: wsId
    });
}
function useRowColumns(programId, teamName, withHandling, clins, onHandling, onLogRequest, silentFor) {
    const params = useCmDrawerParams();
    const cols = [
        {
            id: 'title',
            header: 'Deliverable',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "font-medium",
                    children: /*#__PURE__*/ _jsx(GlossaryText, {
                        text: r.duty.title
                    })
                })
        },
        {
            id: 'source',
            header: 'Source',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(DutySourceChips, {
                            programId: programId,
                            origin: r.duty.origin,
                            pwsRef: r.duty.pwsRef,
                            letter: r.duty.letter,
                            dutyKey: r.duty.dutyKey,
                            sourceDocumentId: r.duty.sourceDocumentId,
                            clins: clins,
                            testIds: {
                                pws: Testids.cmWorkstream.sourcePws,
                                clin: Testids.cmWorkstream.sourceClin,
                                sourceDoc: Testids.cmWorkstream.sourceDoc
                            }
                        }),
                        /*#__PURE__*/ _jsx(CmSourceButton, {
                            locator: r.duty.sourceLocator
                        })
                    ]
                })
        }
    ];
    if (withHandling) cols.push({
        id: 'handling',
        header: 'Handling here',
        cell: (r)=>{
            const a = r.assignment;
            const from = (r.routedFrom ?? []).map((id)=>routedLink(programId, id, `Routed from ${teamName(id)}`, Testids.cmWorkstream.routedFrom));
            if (!a) {
                // SAD-969: owed per the award until the SOW's silence is answered.
                const silent = silentFor?.(r);
                return /*#__PURE__*/ _jsxs("span", {
                    className: "flex flex-wrap items-center gap-1.5",
                    children: [
                        from.length ? from : /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: "Award’s schedule"
                        }),
                        silent ? /*#__PURE__*/ _jsx(SowSilentLink, {
                            href: silent.href,
                            testId: Testids.cmWorkstream.sowSilent
                        }) : null
                    ]
                });
            }
            const quote = a.overrideLocators.find((l)=>l.quote)?.quote;
            const text = a.handling === 'routed' ? `Routed to ${a.routedToWorkstreamId ? teamName(a.routedToWorkstreamId) : 'base scope'}` : a.handling === 'not_owed' ? `Not owed: ${NOT_OWED_REASON_LABEL[a.notOwedReason] ?? a.notOwedReason}` : `${HANDLING_LABEL.performed}${a.teamRules.length ? ' · team rule' : ''}`;
            return /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-1.5",
                "data-testid": Testids.cmWorkstream.handling,
                children: [
                    onHandling ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        className: "text-left",
                        title: "Change how this SOW handles it",
                        onClick: (e)=>{
                            e.stopPropagation();
                            onHandling(r);
                        },
                        children: text
                    }) : text,
                    /*#__PURE__*/ _jsx(CmSourceButton, {
                        locator: a.sourceLocator,
                        quote: quote
                    }),
                    from
                ]
            });
        }
    });
    cols.push({
        id: 'next',
        header: 'Next / state',
        sortable: true,
        align: 'right',
        cell: (r)=>r.assignment?.handling === 'routed' && r.assignment.routedToWorkstreamId ? // SAD-968: the routed team owes it from then on; any date left here is
            // one this team filed before the routing.
            /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center justify-end gap-1",
                children: [
                    r.next ? /*#__PURE__*/ _jsx("span", {
                        className: "tabular-nums text-muted-foreground",
                        children: cmDateShort(r.next.dueOn)
                    }) : null,
                    routedLink(programId, r.assignment.routedToWorkstreamId, `Routed to ${teamName(r.assignment.routedToWorkstreamId)}`, Testids.cmWorkstream.routedTo)
                ]
            }) : r.next ? /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center justify-end gap-1",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        className: "gap-2",
                        onClick: (e)=>{
                            e.stopPropagation();
                            params.open('occ', r.next?.id ?? '');
                        },
                        "data-testid": Testids.cmWorkstream.nextState,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums text-muted-foreground",
                                children: cmDateShort(r.next.dueOn)
                            }),
                            /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: occurrenceStatus(r.next, r.duty.finalReviewTier)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                        programId: programId,
                        occurrenceId: r.next.id
                    }),
                    /*#__PURE__*/ _jsx(CmPendingSubmission, {
                        occ: r.next
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center justify-end gap-1",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "inline-flex justify-end",
                        "data-testid": Testids.cmWorkstream.undatedState,
                        children: r.undated ? /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: r.undated
                        }) : 'No dates'
                    }),
                    r.onRequest && onLogRequest ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: (e)=>{
                            e.stopPropagation();
                            onLogRequest(r);
                        },
                        "data-testid": Testids.cmWorkstream.logRequest,
                        children: "Log request"
                    }) : null
                ]
            })
    });
    return cols;
}
/**
 * SAD-978: a watched row's columns: the deliverable and its source, then the
 * team that owes it and that team's next date. That team files, waives and
 * moves its dates; here the date is no button and no verb is offered.
 */ function watchColumns(base, programId, teamName) {
    return [
        ...base.filter((c)=>c.id === 'title' || c.id === 'source'),
        {
            id: 'owner',
            header: 'Owed by',
            cell: (r)=>r.contractLevel ? /*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.cmWorkstream.watchOwner,
                    children: `Contract-level, owed by the contract${sharedNote(r)}`
                }) : r.watchOwner ? routedLink(programId, r.watchOwner, r.shared ? `Owed by ${teamName(r.watchOwner)}${sharedNote(r)}` : `Owed by ${teamName(r.watchOwner)} · routed by your SOW${sharedNote(r)}`, Testids.cmWorkstream.watchOwner) : null
        },
        {
            id: 'next',
            header: 'Their next date',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "inline-flex flex-wrap items-center justify-end gap-2",
                    "data-testid": Testids.cmWorkstream.watchState,
                    children: r.next ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums text-muted-foreground",
                                children: cmDateShort(r.next.dueOn)
                            }),
                            /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: occurrenceStatus(r.next, r.duty.finalReviewTier)
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        children: "No dates yet"
                    })
                })
        }
    ];
}
/** Apply shared search / filter / sort to a team's deliverable rows. */ function applyDutyRowControls(rows, filters, sort, clins) {
    return sortDeliverableRows(filterDeliverableRows(rows, filters, clins), sort, clins);
}
function dutyTableSortProps(sort, setSort) {
    return {
        sortColumn: sort?.key === 'clin' || sort?.key === 'pws' ? 'source' : sort?.key,
        sortDirection: sort ? toUiSortDir(sort.dir) : undefined,
        onSortChange: (columnId, direction)=>{
            const key = columnId === 'source' ? 'clin' : columnId;
            setSort({
                key,
                dir: fromUiSortDir(direction)
            });
        }
    };
}
function StateFilterList({ programId, occs, finalTierOf }) {
    const [sp, setSp] = useSearchParams();
    const lk = useCmLookups(programId);
    const params = useCmDrawerParams();
    const state = sp.get('state') ?? '';
    if (!state) return null;
    const rows = occs.filter((o)=>(o.status?.state ?? '') === state);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isOccurrenceState(state) ? STATE_SHORT_LABEL[state] : state,
                        " · ",
                        rows.length
                    ]
                }),
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    onClick: ()=>setSp((prev)=>{
                            const n = new URLSearchParams(prev);
                            n.delete('state');
                            return n;
                        }),
                    children: "Clear"
                })
            }),
            /*#__PURE__*/ _jsx(CmStateTable, {
                programId: programId,
                occurrences: rows,
                dutyTitle: (id)=>lk.dutyById.get(id)?.title,
                finalTierOf: finalTierOf,
                teamName: lk.teamName,
                onOpen: (id)=>params.open('occ', id),
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "Nothing in this state.",
                    description: "No date in this window is in it right now."
                })
            })
        ]
    });
}
function TeamView({ wsId }) {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const docs = useProgramDocuments(programId);
    const holdOf = useConditionHolds(programId, [
        ...lk.checkCodes.values()
    ].some((c)=>c.includes(CONDITION_NOT_SETTLED)));
    const ws = useWorkstream(programId, wsId);
    const [periodFilter, setPeriodFilter] = useState('');
    // R6-4: a picked contract period reads that period of the team (its
    // history once its SOWs have ended); none picked, its current window. An
    // undated period has no span to read, so it only filters the rows.
    const picked = lk.periodById.get(periodFilter);
    const readPeriod = picked?.start && picked.end ? periodFilter : '';
    const teamWindow = readPeriod ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: readPeriod
    } : {
        window: OccurrenceWindow.WORKSTREAM_CURRENT_WINDOW
    };
    const occs = useOccurrences(programId, {
        workstreamId: wsId,
        ...teamWindow,
        includeProjected: true
    });
    const [addSow, setAddSow] = useState(null);
    const [closedSows, setClosedSows] = useState([]);
    const [expandedSows, setExpandedSows] = useState([]);
    const [linking, setLinking] = useState(false);
    const [query, setQuery] = useState('');
    const [clinFilter, setClinFilter] = useState('');
    const [pwsFilter, setPwsFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceDocFilter, setSourceDocFilter] = useState('');
    const [sort, setSort] = useState(null);
    // This team's own SOW deliverables' assignments, in one ListDuties read
    // (SAD-994). Only those rows read a deliverable's detail (bySow below);
    // routed-in, watch and unhandled rows come from the lookups and the
    // dates, so pangaea plans no other deliverable (SAD-997).
    const details = useDutiesDetail(programId, {
        workstreamId: wsId
    });
    // SAD-969: this team's SOWs' open "doesn't mention this" questions.
    const silentOf = useSowSilentQuestions(programId);
    const teamSowIds = new Set((ws.data?.sows ?? []).map((s)=>s.id));
    // SAD-967: the deliverable a government request is being logged for.
    const [requestFor, setRequestFor] = useState(null);
    const columns = useRowColumns(programId, lk.teamName, true, clins, access.canWrite ? (r)=>r.assignment && params.open('assign', `${wsId}:${r.assignment.sowId}:${r.duty.id}`) : undefined, access.canWrite ? (r)=>setRequestFor(r.duty.id) : undefined, (r)=>silentOf(r.duty.id).find((q)=>teamSowIds.has(q.sowId)));
    // SAD-978: deliverables this team's SOW routed to another team, which it
    // watches read-only, by duty: the teams that owe them, and their dates.
    const watchOwners = new Map();
    // Owner ruling 1: a deliverable routed to the base scope is the
    // contract's (no owner team): its contract-level dates, by duty.
    const contractWatched = new Set();
    // R12-C: the teams that see each (deliverable, owner) series, and the
    // (deliverable, owner) series this team shares rather than routes.
    const seenBy = new Map();
    const sharedHere = new Set();
    for (const w of lk.watches){
        const k = `${w.dutyId}|${w.ownerWorkstreamId}`;
        seenBy.set(k, (seenBy.get(k) ?? new Set()).add(w.watcherWorkstreamId));
        if (w.watcherWorkstreamId === wsId && w.disposition === ExecutionPerformanceDisposition.SHARED_DELIVERY) sharedHere.add(k);
    }
    const sharedFields = (dutyId, owner)=>({
            sharedWith: seenBy.get(`${dutyId}|${owner}`)?.size,
            shared: sharedHere.has(`${dutyId}|${owner}`)
        });
    // R16-A: a watch holds the term of this team's SOW that routes or shares
    // the deliverable, by (deliverable, owner): a date outside it is not
    // this team's, and a row whose dates all fall outside it is not shown.
    const termsOf = new Map();
    for (const w of lk.watches){
        if (w.watcherWorkstreamId !== wsId) continue;
        const k = `${w.dutyId}|${w.ownerWorkstreamId}`;
        termsOf.set(k, [
            ...termsOf.get(k) ?? [],
            {
                from: w.fromOn,
                to: w.toOn
            }
        ]);
    }
    const inTerm = (dutyId, owner)=>(o)=>{
            const day = o.nominalDueOn || o.dueOn;
            return (termsOf.get(`${dutyId}|${owner}`) ?? []).some((t)=>!day || (!t.from || day >= t.from) && (!t.to || day <= t.to));
        };
    for (const w of lk.watches){
        if (w.watcherWorkstreamId !== wsId) continue;
        if (!w.ownerWorkstreamId) {
            contractWatched.add(w.dutyId);
            continue;
        }
        const arr = watchOwners.get(w.dutyId) ?? [];
        if (!arr.includes(w.ownerWorkstreamId)) arr.push(w.ownerWorkstreamId);
        watchOwners.set(w.dutyId, arr);
    }
    const ownerIds = [
        ...new Set([
            ...watchOwners.values()
        ].flat())
    ].sort();
    const ownerOccs = useOccurrencesMany(programId, ownerIds.map((id)=>({
            workstreamId: id,
            ...teamWindow
        })));
    const watchRows = [];
    ownerIds.forEach((owner, i)=>{
        const byDuty = byDutyId((ownerOccs[i]?.data?.occurrences ?? []).filter((o)=>!o.event?.projected && !o.retired));
        for (const [dutyId, owners] of watchOwners){
            const duty = lk.dutyById.get(dutyId);
            if (!duty || !owners.includes(owner)) continue;
            const all = byDuty.get(dutyId) ?? [];
            const firm = all.filter(inTerm(dutyId, owner));
            if (all.length && !firm.length) continue;
            const next = nextOf(firm);
            watchRows.push({
                duty,
                next,
                ...dutyRowFields(duty, next, owner, firm, lk.periods, lk.exercises),
                watchOwner: owner,
                ...sharedFields(dutyId, owner)
            });
        }
    });
    const contractDuties = [
        ...contractWatched
    ].sort();
    const contractOccs = useOccurrencesMany(programId, contractDuties.map((dutyId)=>({
            dutyId,
            ...readPeriod ? {
                window: OccurrenceWindow.CONTRACT_PERIOD,
                periodOfPerformanceId: readPeriod
            } : {
                window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
            }
        })));
    contractDuties.forEach((dutyId, i)=>{
        const duty = lk.dutyById.get(dutyId);
        if (!duty) return;
        const all = (contractOccs[i]?.data?.occurrences ?? []).filter((o)=>!o.workstreamId && !o.event?.projected && !o.retired);
        const firm = all.filter(inTerm(dutyId, ''));
        if (all.length && !firm.length) return;
        const next = nextOf(firm);
        watchRows.push({
            duty,
            next,
            ...dutyRowFields(duty, next, '', firm, lk.periods, lk.exercises),
            contractLevel: true,
            ...sharedFields(dutyId, '')
        });
    });
    watchRows.sort(byNextThenTitle);
    const ruleGap = useRuleSowGap(programId, {
        success: 'Ruling recorded'
    });
    const [sp, setSp] = useSearchParams();
    const tab = [
        'work',
        'members'
    ].includes(sp.get('tab') ?? '') ? sp.get('tab') : 'deliverables';
    const setTab = (next)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (next === 'deliverables') n.delete('tab');
            else n.set('tab', next);
            n.delete('state');
            return n;
        });
    const workCount = useWorkProducts(programId, {
        workstreamId: wsId
    }).data?.length;
    const memberCount = useWorkstreamMembers(programId, wsId).data?.length;
    const [linkingLines, setLinkingLines] = useState(false);
    const kpi = useTeamKpi(programId, wsId, {
        enabled: (ws.data?.exerciseLines ?? []).length > 0 || (ws.data?.workstream?.lineNumbers ?? []).length > 0,
        periodId: readPeriod
    });
    const data = ws.data;
    const sows = useMemo(()=>[
            ...data?.sows ?? []
        ].sort((a, b)=>(b.popStart || '').localeCompare(a.popStart || '')), [
        data?.sows
    ]);
    const lines = data?.exerciseLines ?? [];
    // SAD-861: base and option-period lines the team owns.
    const ownedLines = data?.workstream?.lineNumbers ?? [];
    const lineOts = new Set(lines.map((l)=>l.optionalTaskId));
    const allOccs = occs.data?.occurrences ?? [];
    // Projected rows are not occurrences: the state filter and every verb read firm rows.
    const occList = useMemo(()=>allOccs.filter((o)=>!o.event?.projected), [
        allOccs
    ]);
    const { bySow, unhandled } = useMemo(()=>{
        const sowIds = new Set(sows.map((s)=>s.id));
        const bySow = new Map();
        const handled = new Set();
        const occByDuty = byDutyId(allOccs);
        // SAD-968: deliverables another team's SOW routes here, by duty: the
        // routings ListDuties already names (SAD-975), never a GetWorkstream per
        // team to find each SOW's team (SAD-993).
        const routedFrom = new Map();
        for (const duty of lk.duties){
            for (const r of lk.routingsByDuty.get(duty.id) ?? []){
                const from = r.fromWorkstreamId;
                if (r.toWorkstreamId !== wsId || !from || from === wsId) continue;
                const arr = routedFrom.get(duty.id) ?? [];
                if (!arr.includes(from)) arr.push(from);
                routedFrom.set(duty.id, arr);
            }
        }
        const rowOf = (duty, assignment)=>{
            const dutyOccs = occByDuty.get(duty.id) ?? [];
            const { firm, projected } = splitProjected(dutyOccs);
            const next = nextOf(firm);
            // The SOW's team-rule row when it sets one, else the award's.
            const reason = lk.undatedReason(duty.id, assignment?.id);
            return {
                duty,
                assignment,
                next,
                ...dutyRowFields(duty, next, wsId, dutyOccs, lk.periods, lk.exercises),
                undated: next ? undefined : undatedState(duty, reason, lk.checkCodes.get(duty.id) ?? [], projected, holdOf),
                onRequest: isGovernmentRequest(reason),
                routedFrom: routedFrom.get(duty.id)
            };
        };
        lk.duties.forEach((duty)=>{
            const assignments = details.data?.byId.get(duty.id)?.assignments ?? [];
            for (const a of assignments){
                if (!sowIds.has(a.sowId)) continue;
                handled.add(duty.id);
                const arr = bySow.get(a.sowId) ?? [];
                arr.push(rowOf(duty, a));
                bySow.set(a.sowId, arr);
            }
        });
        const owedHere = lk.duties.filter((d)=>!handled.has(d.id) && (occByDuty.has(d.id) || routedFrom.has(d.id) || (lk.rulesByDuty.get(d.id) ?? []).some((r)=>r.optionalTaskId && lineOts.has(r.optionalTaskId))));
        return {
            bySow,
            unhandled: owedHere.map((duty)=>rowOf(duty)).sort(byNextThenTitle)
        };
    }, [
        sows,
        lk.duties,
        lk.rulesByDuty,
        lk.checkCodes,
        lk.undatedReason,
        lk.routingsByDuty,
        lk.periods,
        lk.exercises,
        details.data,
        allOccs,
        lineOts,
        holdOf,
        wsId
    ]);
    const tableFilters = useMemo(()=>({
            q: query,
            clin: clinFilter,
            pws: pwsFilter,
            status: statusFilter,
            sourceDoc: sourceDocFilter,
            period: periodFilter
        }), [
        query,
        clinFilter,
        pwsFilter,
        statusFilter,
        sourceDocFilter,
        periodFilter
    ]);
    const allDutyRows = useMemo(()=>{
        const out = [];
        for (const rows of bySow.values())out.push(...rows);
        out.push(...unhandled);
        return out;
    }, [
        bySow,
        unhandled
    ]);
    const filterOptions = useMemo(()=>{
        const docById = new Map((docs.data?.items ?? []).map((d)=>[
                d.id,
                d
            ]));
        return {
            clins: clinFilterOptions(allDutyRows.map((r)=>r.dutyKey), clins).map((v)=>({
                    value: v,
                    label: v
                })),
            pws: uniqueSorted(allDutyRows.map((r)=>r.pwsRef)).map((v)=>({
                    value: v,
                    label: `PWS ${v}`
                })),
            statuses: uniqueSorted(allDutyRows.map((r)=>r.statusState ?? '')).filter((s)=>isOccurrenceState(s)).map((s)=>({
                    value: s,
                    label: STATE_SHORT_LABEL[s]
                })),
            sourceDocs: uniqueSorted(allDutyRows.map((r)=>r.sourceDocumentId ?? '').filter(Boolean)).map((id)=>{
                const d = docById.get(id);
                return {
                    value: id,
                    label: sourceDocChipLabel(d ? {
                        id,
                        title: d.title,
                        fileName: d.fileName,
                        category: d.category
                    } : {
                        id
                    }, 'award')
                };
            }),
            periods: lk.periods.map((p)=>({
                    value: p.id,
                    label: p.name
                }))
        };
    }, [
        allDutyRows,
        clins,
        docs.data,
        lk.periods
    ]);
    const sortProps = dutyTableSortProps(sort, setSort);
    if (ws.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: ws.error,
                title: "Could not load this team",
                onRetry: ()=>void ws.refetch()
            })
        });
    }
    const team = data?.workstream;
    // A deliverable whose detail fails to load has no SOW rows here; say which,
    // never leave it silently missing. The read lists only this team's SOW
    // deliverables (SAD-997), so a listed one without its detail is missing.
    const missingDetail = details.isError ? lk.duties : details.isSuccess && !details.isFetching ? details.data.listed.filter((d)=>!details.data.byId.has(d.id)) : [];
    const finalTierOf = (dutyId)=>lk.dutyById.get(dutyId)?.finalReviewTier ?? 'va_pm';
    const laterSowFor = (gap)=>[
            ...sows
        ].sort((a, b)=>(a.popStart || '').localeCompare(b.popStart || '')).find((s)=>(s.popStart || '') > gap.fromOn);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
                "data-testid": Testids.cmWorkstream.page,
                metrics: lines.length > 0 || ownedLines.length > 0 ? kpi.strip : undefined,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                            renderLink: renderRouterLink,
                            items: [
                                {
                                    id: 'teams',
                                    label: 'Teams',
                                    href: `/delivery/programs/${programId}/workstreams`
                                },
                                {
                                    id: 'team',
                                    label: team?.name ?? 'Team',
                                    current: true
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: team?.name ?? 'Team',
                            actions: /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center justify-end gap-2",
                                children: [
                                    access.canCreate ? /*#__PURE__*/ _jsx(UploadSowButton, {
                                        programId: programId,
                                        workstreamId: wsId
                                    }) : null,
                                    access.canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setLinkingLines(true),
                                        "data-testid": Testids.cmWorkstream.linkLines,
                                        children: [
                                            /*#__PURE__*/ _jsx(Link2, {
                                                className: "h-4 w-4",
                                                "aria-hidden": true
                                            }),
                                            " Base lines"
                                        ]
                                    }) : null,
                                    access.canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setLinking(true),
                                        "data-testid": Testids.cmWorkstream.linkExercise,
                                        children: [
                                            /*#__PURE__*/ _jsx(Link2, {
                                                className: "h-4 w-4",
                                                "aria-hidden": true
                                            }),
                                            " Link exercise"
                                        ]
                                    }) : null,
                                    access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>params.open('submit', 'new', {
                                                forTeam: wsId
                                            }),
                                        "data-testid": Testids.cmWorkstream.submit,
                                        children: "+ Submit"
                                    }) : null
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 text-[length:var(--fs-ui-body)]",
                            "data-testid": Testids.cmWorkstream.header,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Lines:"
                                        }),
                                        ownedLines.length === 0 ? /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "no base-period lines; its deliverables there are contract-wide"
                                        }) : /*#__PURE__*/ _jsx(TeamLinesTags, {
                                            lines: ownedLines
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Exercises:"
                                        }),
                                        lines.length === 0 ? /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "none linked"
                                        }) : lines.map((l)=>/*#__PURE__*/ _jsxs("span", {
                                                className: "inline-flex items-center gap-1.5",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraTag, {
                                                        "data-testid": Testids.cmWorkstream.exerciseLink,
                                                        children: [
                                                            lk.optionalTaskById.get(l.optionalTaskId)?.short ?? 'OT',
                                                            " ",
                                                            l.clinNumber
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            "PoP ",
                                                            cmSpan(l.popStart, l.popEnd)
                                                        ]
                                                    })
                                                ]
                                            }, l.slinId || l.exerciseId))
                                    ]
                                }),
                                (data?.predecessors ?? []).length > 0 ? /*#__PURE__*/ _jsxs("div", {
                                    "data-testid": Testids.cmWorkstream.lineage,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Continues: "
                                        }),
                                        (data?.predecessors ?? []).map((p, i)=>/*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    i > 0 ? ' + ' : '',
                                                    /*#__PURE__*/ _jsx(SpecraLink, {
                                                        href: `/delivery/programs/${programId}/workstreams/${p.predecessorWorkstreamId}`,
                                                        renderLink: renderRouterLink,
                                                        children: lk.teamName(p.predecessorWorkstreamId)
                                                    })
                                                ]
                                            }, p.id)),
                                        data?.predecessors[0]?.effectiveOn ? /*#__PURE__*/ _jsxs("span", {
                                            className: "text-muted-foreground",
                                            children: [
                                                ' ',
                                                "— from ",
                                                cmDate(data.predecessors[0].effectiveOn)
                                            ]
                                        }) : null
                                    ]
                                }) : null
                            ]
                        })
                    ]
                }),
                children: [
                    /*#__PURE__*/ _jsx(SpecraTabBar, {
                        label: "Team views",
                        value: tab,
                        onValueChange: setTab,
                        "data-testid": Testids.cmWorkProducts.teamTabs,
                        options: [
                            {
                                value: 'deliverables',
                                label: 'Deliverables'
                            },
                            {
                                value: 'work',
                                label: 'Work products',
                                count: workCount
                            },
                            {
                                value: 'members',
                                label: 'Members',
                                count: memberCount
                            }
                        ]
                    }),
                    tab === 'work' ? /*#__PURE__*/ _jsx(WorkProductsPanel, {
                        programId: programId,
                        wsId: wsId,
                        teamName: team?.name ?? 'This team'
                    }) : tab === 'members' ? /*#__PURE__*/ _jsx(TeamMembersPanel, {
                        programId: programId,
                        wsId: wsId
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            lines.length > 0 || ownedLines.length > 0 ? kpi.windowNote : null,
                            /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                                programId: programId,
                                team: wsId
                            }),
                            missingDetail.length > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                tone: "warning",
                                variant: "framed",
                                "data-testid": Testids.cmWorkstream.dutyDetailMissing,
                                title: `${missingDetail.length} deliverable${missingDetail.length === 1 ? '' : 's'} could not be loaded`,
                                children: [
                                    "Their SOW assignments are missing from this page:",
                                    ' ',
                                    missingDetail.map((d)=>d.title || d.dutyKey).join(', '),
                                    "."
                                ]
                            }) : null,
                            (data?.openGaps ?? []).map((gap)=>{
                                const later = laterSowFor(gap);
                                return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                    tone: "warning",
                                    variant: "framed",
                                    "data-testid": Testids.cmWorkstream.gapBanner,
                                    action: access.canWrite ? /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap gap-2",
                                        children: [
                                            later ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "secondary",
                                                "data-testid": Testids.cmWorkstream.gapChoice,
                                                "data-choice": "latest_sow",
                                                onClick: ()=>ruleGap.mutate({
                                                        workstreamId: wsId,
                                                        fromOn: gap.fromOn,
                                                        toOn: gap.toOn,
                                                        choice: SowGapChoice.LATEST_SOW,
                                                        sowId: later.id
                                                    }),
                                                children: "The latest SOW applied all along"
                                            }) : null,
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "secondary",
                                                "data-testid": Testids.cmWorkstream.gapChoice,
                                                "data-choice": "award",
                                                onClick: ()=>ruleGap.mutate({
                                                        workstreamId: wsId,
                                                        fromOn: gap.fromOn,
                                                        toOn: gap.toOn,
                                                        choice: SowGapChoice.AWARD
                                                    }),
                                                children: "The award’s schedule applied"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "secondary",
                                                "data-testid": Testids.cmWorkstream.gapChoice,
                                                "data-choice": "upload_earlier",
                                                onClick: ()=>setAddSow({
                                                        gap
                                                    }),
                                                children: "Add the earlier version"
                                            })
                                        ]
                                    }) : undefined,
                                    children: [
                                        "No SOW covers ",
                                        cmSpan(gap.fromOn, gap.toOn),
                                        ". Until someone says which schedule applied, deliverables the later SOW changes have no dates in those months."
                                    ]
                                }, `${gap.fromOn}-${gap.toOn}`);
                            }),
                            (lines.length > 0 || ownedLines.length > 0) && sows.length === 0 ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "info",
                                variant: "framed",
                                "data-testid": Testids.cmWorkstream.notOnFileBanner,
                                action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    onClick: ()=>setAddSow({}),
                                    "data-testid": Testids.cmWorkstream.addSow,
                                    children: "Add SOW"
                                }) : undefined,
                                children: "No SOW on file: the award’s schedule governs this team’s deliverables."
                            }) : null,
                            lines.length === 0 && ownedLines.length === 0 && !ws.isLoading ? /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.cmWorkstream.noExercises,
                                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    framed: true,
                                    title: "Link an exercise line to start this team’s schedule",
                                    description: "An exercise line (e.g. 11001-17) authorizes a team. Until one is linked, nothing is owed here.",
                                    action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>setLinking(true),
                                        children: "Link exercise"
                                    }) : undefined
                                })
                            }) : null,
                            /*#__PURE__*/ _jsx(StateFilterList, {
                                programId: programId,
                                occs: occList,
                                finalTierOf: finalTierOf
                            }),
                            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                                search: {
                                    value: query,
                                    onChange: (e)=>setQuery(e.target.value),
                                    onClear: ()=>setQuery(''),
                                    placeholder: 'Search deliverables…',
                                    'data-testid': Testids.cmWorkstream.search
                                },
                                filters: /*#__PURE__*/ _jsx(DeliverableFilters, {
                                    values: {
                                        clin: clinFilter,
                                        pws: pwsFilter,
                                        status: statusFilter,
                                        sourceDoc: sourceDocFilter,
                                        period: periodFilter
                                    },
                                    onChange: (key, value)=>{
                                        if (key === 'clin') setClinFilter(value);
                                        else if (key === 'pws') setPwsFilter(value);
                                        else if (key === 'status') setStatusFilter(value);
                                        else if (key === 'sourceDoc') setSourceDocFilter(value);
                                        else if (key === 'period') setPeriodFilter(value);
                                    },
                                    options: filterOptions,
                                    show: {
                                        team: false
                                    },
                                    periodAllLabel: "Current window",
                                    testIds: {
                                        clin: Testids.cmWorkstream.clinFilter,
                                        pws: Testids.cmWorkstream.pwsFilter,
                                        status: Testids.cmWorkstream.statusFilter,
                                        sourceDoc: Testids.cmWorkstream.sourceDocFilter,
                                        period: Testids.cmWorkstream.periodFilter
                                    }
                                })
                            }),
                            sows.length > 0 ? /*#__PURE__*/ _jsx(SpecraRecordGroups, {
                                className: "space-y-5 sm:space-y-6",
                                variant: "hairline",
                                openIds: sows.filter((sow)=>!closedSows.includes(sow.id)).map((sow)=>sow.id),
                                onOpenChange: (ids)=>setClosedSows(sows.filter((sow)=>!ids.includes(sow.id)).map((sow)=>sow.id)),
                                getGroupTestId: ()=>Testids.cmWorkstream.sowBlock,
                                getGroupProps: (group)=>({
                                        'data-row-id': group.id
                                    }),
                                groups: sows.map((sow)=>{
                                    const all = (bySow.get(sow.id) ?? []).sort(byNextThenTitle);
                                    const performed = applyDutyRowControls(all.filter(owedByTeam), tableFilters, sort, clins);
                                    const notPerformed = applyDutyRowControls(all.filter((r)=>!owedByTeam(r)), tableFilters, sort, clins);
                                    const showAll = expandedSows.includes(sow.id);
                                    const rows = showAll ? [
                                        ...performed,
                                        ...notPerformed
                                    ] : performed;
                                    return {
                                        id: sow.id,
                                        header: {
                                            title: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    "SOW",
                                                    sow.version ? ` · v${sow.version}` : ''
                                                ]
                                            }),
                                            actions: access.canCreate && sow === sows[0] || access.canWrite ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    access.canCreate && sow === sows[0] ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                        size: "sm",
                                                        variant: "ghost",
                                                        onClick: ()=>setAddSow({}),
                                                        "data-testid": Testids.cmWorkstream.addSow,
                                                        children: "Add SOW version"
                                                    }) : null,
                                                    access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                        size: "sm",
                                                        variant: "secondary",
                                                        onClick: ()=>params.open('assign', `${wsId}:${sow.id}:new`),
                                                        "data-testid": Testids.cmWorkstream.handleDuty,
                                                        children: "+ Handle an award deliverable"
                                                    }) : null
                                                ]
                                            }) : undefined
                                        },
                                        content: /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                                    as: "h3",
                                                    size: "sm",
                                                    children: sow.title
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex flex-wrap items-center gap-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SowStateBadge, {
                                                            state: sow.state
                                                        }),
                                                        sow.approvedOn ? /*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                "Approved ",
                                                                cmDate(sow.approvedOn)
                                                            ]
                                                        }) : null,
                                                        /*#__PURE__*/ _jsx("span", {
                                                            children: cmSpan(sow.popStart, sow.popEnd)
                                                        }),
                                                        sow.state === 'approved_waiting' ? /*#__PURE__*/ _jsx("span", {
                                                            children: "Nothing is scheduled until an exercise line covers these dates."
                                                        }) : null
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(CmTable, {
                                                    caption: `Deliverables under ${sow.title}`,
                                                    rows: rows,
                                                    columns: columns,
                                                    rowKey: (r)=>r.duty.id,
                                                    rowTestId: Testids.cmWorkstream.dutyRow,
                                                    onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                                                    getRowLabel: (r)=>r.duty.title,
                                                    ...sortProps,
                                                    empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                                        size: "compact",
                                                        title: all.length > 0 ? 'No deliverables performed here.' : 'No deliverables handled here yet.',
                                                        description: all.length > 0 ? 'Every deliverable this SOW handles is marked not performed here — use the link below to see them.' : 'This SOW says nothing about an award deliverable yet.',
                                                        action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                            size: "inline",
                                                            variant: "link",
                                                            onClick: ()=>params.open('assign', `${wsId}:${sow.id}:new`),
                                                            children: "Handle an award deliverable"
                                                        }) : undefined
                                                    })
                                                }),
                                                notPerformed.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    onClick: ()=>setExpandedSows((ids)=>showAll ? ids.filter((id)=>id !== sow.id) : [
                                                                ...ids,
                                                                sow.id
                                                            ]),
                                                    "data-testid": Testids.cmWorkstream.notPerformedToggle,
                                                    children: showAll ? 'Hide not performed here' : `Show ${notPerformed.length} not performed here`
                                                }) : null
                                            ]
                                        })
                                    };
                                })
                            }) : null,
                            unhandled.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                                className: "space-y-2",
                                "data-testid": Testids.cmWorkstream.unhandledSection,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                        variant: "group",
                                        title: sows.length > 0 ? `Award deliverables with no handling recorded here · ${applyDutyRowControls(unhandled, tableFilters, sort, clins).length}` : `Owed under the award’s schedule · ${applyDutyRowControls(unhandled, tableFilters, sort, clins).length}`
                                    }),
                                    /*#__PURE__*/ _jsx(CmTable, {
                                        caption: "Award deliverables owed by this team",
                                        rows: applyDutyRowControls(unhandled, tableFilters, sort, clins),
                                        columns: columns,
                                        rowKey: (r)=>r.duty.id,
                                        rowTestId: Testids.cmWorkstream.dutyRow,
                                        onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                                        getRowLabel: (r)=>r.duty.title,
                                        ...sortProps
                                    })
                                ]
                            }) : null,
                            watchRows.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                                className: "space-y-2",
                                "data-testid": Testids.cmWorkstream.watchSection,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                        variant: "group",
                                        title: `Routed by your SOW · ${watchRows.length}`
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "caption",
                                        tone: "muted",
                                        children: "Your SOW hands these to another team or to the contract. You see their dates and status here and hear when one is late; whoever owes it files it."
                                    }),
                                    /*#__PURE__*/ _jsx(CmTable, {
                                        caption: "Deliverables your SOW routes to another team",
                                        rows: watchRows,
                                        columns: watchColumns(columns, programId, lk.teamName),
                                        rowKey: (r)=>`${r.duty.id}:${r.watchOwner}`,
                                        rowTestId: Testids.cmWorkstream.watchRow,
                                        getRowLabel: (r)=>r.duty.title
                                    })
                                ]
                            }) : null
                        ]
                    })
                ]
            }),
            addSow ? /*#__PURE__*/ _jsx(AddSowDrawer, {
                programId: programId,
                workstreamId: wsId,
                gap: addSow.gap,
                onClose: ()=>setAddSow(null)
            }) : null,
            linking ? /*#__PURE__*/ _jsx(LinkExerciseDrawer, {
                programId: programId,
                workstreamId: wsId,
                onClose: ()=>setLinking(false)
            }) : null,
            linkingLines ? /*#__PURE__*/ _jsx(TeamLinesDrawer, {
                programId: programId,
                workstreamId: wsId,
                lines: ownedLines,
                onClose: ()=>setLinkingLines(false)
            }) : null,
            requestFor ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: "on_request",
                dutyId: requestFor,
                workstreamId: wsId,
                onClose: ()=>setRequestFor(null)
            }) : null
        ]
    });
}
function ContractWideView() {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const docs = useProgramDocuments(programId);
    const holdOf = useConditionHolds(programId, [
        ...lk.checkCodes.values()
    ].some((c)=>c.includes(CONDITION_NOT_SETTLED)));
    const label = useContractWideLabel(programId);
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        includeProjected: true
    });
    const [query, setQuery] = useState('');
    const [clinFilter, setClinFilter] = useState('');
    const [pwsFilter, setPwsFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceDocFilter, setSourceDocFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState('');
    const [sort, setSort] = useState(null);
    const access = useCmAccess(programId);
    const [requestFor, setRequestFor] = useState(null);
    const columns = useRowColumns(programId, lk.teamName, false, clins, undefined, access.canWrite ? (r)=>setRequestFor(r.duty.id) : undefined);
    const window = occs.data?.window;
    const wideAll = useMemo(()=>(occs.data?.occurrences ?? []).filter((o)=>!o.workstreamId && !o.retired), [
        occs.data
    ]);
    // Projected rows are not occurrences: the strip and the state filter read firm rows.
    const wide = useMemo(()=>wideAll.filter((o)=>!o.event?.projected), [
        wideAll
    ]);
    const rows = useMemo(()=>{
        const occByDuty = byDutyId(wideAll);
        // SAD-842: every deliverable the contract owes in the period, dated or not —
        // an award rule no team's exercise line owns (base, this option period, or
        // whenever the event happens), or no rule at all.
        const pop = window?.periodOfPerformanceId ?? '';
        const from = window?.startOn ?? '';
        const to = window?.endOn ?? '';
        const inWindow = (r)=>(!r.effectiveTo || !from || r.effectiveTo >= from) && (!r.effectiveFrom || !to || r.effectiveFrom <= to);
        const owedWide = (duty, rules)=>{
            if (occByDuty.has(duty.id)) return true;
            if (duty.endedOn && from && duty.endedOn < from) return false;
            if (duty.origin === 'sow') return false;
            if ((lk.rulesByDuty.get(duty.id) ?? []).length === 0) return true;
            return rules.some((r)=>inWindow(r) && (r.gate === 'none' || (r.gate === 'base' || r.gate === 'option_period') && (!pop || r.periodOfPerformanceId === pop)));
        };
        return lk.duties.map((duty)=>({
                duty,
                rules: awardRules(lk.rulesByDuty.get(duty.id) ?? [])
            })).filter(({ duty, rules })=>owedWide(duty, rules)).map(({ duty })=>{
            const dutyOccs = occByDuty.get(duty.id) ?? [];
            const { firm, projected } = splitProjected(dutyOccs);
            const next = nextOf(firm);
            return {
                duty,
                next,
                ...dutyRowFields(duty, next, '', dutyOccs, lk.periods, lk.exercises),
                undated: next ? undefined : undatedState(duty, lk.undatedReason(duty.id), lk.checkCodes.get(duty.id) ?? [], projected, holdOf),
                onRequest: isGovernmentRequest(lk.undatedReason(duty.id))
            };
        }).sort(byNextThenTitle);
    }, [
        wideAll,
        window,
        lk.duties,
        lk.rulesByDuty,
        lk.checkCodes,
        lk.undatedReason,
        lk.periods,
        lk.exercises,
        holdOf
    ]);
    const tableFilters = useMemo(()=>({
            q: query,
            clin: clinFilter,
            pws: pwsFilter,
            status: statusFilter,
            sourceDoc: sourceDocFilter,
            period: periodFilter
        }), [
        query,
        clinFilter,
        pwsFilter,
        statusFilter,
        sourceDocFilter,
        periodFilter
    ]);
    const visible = useMemo(()=>applyDutyRowControls(rows, tableFilters, sort, clins), [
        rows,
        tableFilters,
        sort,
        clins
    ]);
    const filterOptions = useMemo(()=>{
        const docById = new Map((docs.data?.items ?? []).map((d)=>[
                d.id,
                d
            ]));
        return {
            clins: clinFilterOptions(rows.map((r)=>r.dutyKey), clins).map((v)=>({
                    value: v,
                    label: v
                })),
            pws: uniqueSorted(rows.map((r)=>r.pwsRef)).map((v)=>({
                    value: v,
                    label: `PWS ${v}`
                })),
            statuses: uniqueSorted(rows.map((r)=>r.statusState ?? '')).filter((s)=>isOccurrenceState(s)).map((s)=>({
                    value: s,
                    label: STATE_SHORT_LABEL[s]
                })),
            sourceDocs: uniqueSorted(rows.map((r)=>r.sourceDocumentId ?? '').filter(Boolean)).map((id)=>{
                const d = docById.get(id);
                return {
                    value: id,
                    label: sourceDocChipLabel(d ? {
                        id,
                        title: d.title,
                        fileName: d.fileName,
                        category: d.category
                    } : {
                        id
                    }, 'award')
                };
            }),
            periods: lk.periods.map((p)=>({
                    value: p.id,
                    label: p.name
                }))
        };
    }, [
        rows,
        clins,
        docs.data,
        lk.periods
    ]);
    const sortProps = dutyTableSortProps(sort, setSort);
    const counts = {};
    for (const o of wide)counts[o.status?.state ?? ''] = (counts[o.status?.state ?? ''] ?? 0) + 1;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
                "data-testid": Testids.cmWorkstream.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                            renderLink: renderRouterLink,
                            items: [
                                {
                                    id: 'teams',
                                    label: 'Teams',
                                    href: `/delivery/programs/${programId}/workstreams`
                                },
                                {
                                    id: 'team',
                                    label,
                                    current: true
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: label,
                            description: window && window.basis !== 'none' ? `Deliverables the contract owes that no team does · ${cmSpan(window.startOn, window.endOn)}` : 'Deliverables the contract owes that no team does'
                        })
                    ]
                }),
                metrics: occs.isLoading ? LOADING_STRIP : stripProps({
                    counts,
                    states: [
                        'late',
                        'awaiting_review',
                        'rejected',
                        'due_soon'
                    ],
                    windowText: window && window.basis !== 'none' ? `in contract-wide deliverables (${cmSpan(window.startOn, window.endOn)})` : 'in contract-wide deliverables',
                    hrefFor: (state)=>`/delivery/programs/${programId}/workstreams/contract-wide?state=${state}`,
                    testId: Testids.cmWorkstream.strip,
                    tileTestId: Testids.cmWorkstream.stripTile
                }),
                children: [
                    /*#__PURE__*/ _jsx(StateFilterList, {
                        programId: programId,
                        occs: wide,
                        finalTierOf: (id)=>lk.dutyById.get(id)?.finalReviewTier ?? 'va_pm'
                    }),
                    /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                        search: {
                            value: query,
                            onChange: (e)=>setQuery(e.target.value),
                            onClear: ()=>setQuery(''),
                            placeholder: 'Search deliverables…',
                            'data-testid': Testids.cmWorkstream.search
                        },
                        filters: /*#__PURE__*/ _jsx(DeliverableFilters, {
                            values: {
                                clin: clinFilter,
                                pws: pwsFilter,
                                status: statusFilter,
                                sourceDoc: sourceDocFilter,
                                period: periodFilter
                            },
                            onChange: (key, value)=>{
                                if (key === 'clin') setClinFilter(value);
                                else if (key === 'pws') setPwsFilter(value);
                                else if (key === 'status') setStatusFilter(value);
                                else if (key === 'sourceDoc') setSourceDocFilter(value);
                                else if (key === 'period') setPeriodFilter(value);
                            },
                            options: filterOptions,
                            show: {
                                team: false
                            },
                            testIds: {
                                clin: Testids.cmWorkstream.clinFilter,
                                pws: Testids.cmWorkstream.pwsFilter,
                                status: Testids.cmWorkstream.statusFilter,
                                sourceDoc: Testids.cmWorkstream.sourceDocFilter,
                                period: Testids.cmWorkstream.periodFilter
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(CmTable, {
                        caption: "Contract-wide deliverables",
                        rows: visible,
                        columns: columns,
                        rowKey: (r)=>r.duty.id,
                        rowTestId: Testids.cmWorkstream.dutyRow,
                        isLoading: occs.isLoading || lk.isLoading,
                        error: occs.error,
                        onRetry: ()=>void occs.refetch(),
                        onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                        getRowLabel: (r)=>r.duty.title,
                        ...sortProps,
                        empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            framed: true,
                            title: "Nothing contract-wide this period",
                            description: "Every date this period belongs to a team. Contract-wide deliverables (base-period reports, for example) appear here."
                        })
                    })
                ]
            }),
            requestFor ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: "on_request",
                dutyId: requestFor,
                workstreamId: "",
                onClose: ()=>setRequestFor(null)
            }) : null
        ]
    });
}
function AddSowDrawer({ programId, workstreamId, gap, onClose }) {
    const create = useCreateSow(programId, {
        success: 'SOW added',
        errors: 'inline'
    });
    const [title, setTitle] = useState('');
    const [version, setVersion] = useState('');
    const [approvedOn, setApprovedOn] = useState('');
    const [popStart, setPopStart] = useState(gap?.fromOn ?? '');
    const [popEnd, setPopEnd] = useState(gap?.toOn ?? '');
    const ready = title.trim() !== '' && !create.isPending;
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: gap ? 'Add the earlier SOW version' : 'Add SOW',
        description: gap ? `It should cover ${cmSpan(gap.fromOn, gap.toOn)}, the months no SOW on file covers.` : 'The SOW (or EP) this team works to. From its PoP start its terms apply.',
        onClose: onClose,
        busy: create.isPending,
        testId: Testids.cmWorkstream.sowDrawer,
        footerProps: {
            confirmLabel: 'Add SOW',
            onConfirm: ()=>create.mutate({
                    workstreamId,
                    title: title.trim(),
                    version: version.trim(),
                    approvedOn,
                    popStart,
                    popEnd
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: !ready,
            loading: create.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkstream.sowSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    required: true,
                    hint: "As the document titles itself.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmWorkstream.sowTitle
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Version",
                    hint: "e.g. 1.4",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: version,
                        onChange: (e)=>setVersion(e.target.value),
                        "data-testid": Testids.cmWorkstream.sowVersion
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Approved on",
                    value: approvedOn,
                    onValueChange: setApprovedOn,
                    "data-testid": Testids.cmWorkstream.sowApprovedOn
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "PoP start",
                            value: popStart,
                            onValueChange: setPopStart,
                            "data-testid": Testids.cmWorkstream.sowPopStart
                        }),
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "PoP end",
                            value: popEnd,
                            onValueChange: setPopEnd,
                            "data-testid": Testids.cmWorkstream.sowPopEnd
                        })
                    ]
                }),
                create.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(create.error, 'Could not save')
                }) : null
            ]
        })
    });
}
export function LinkExerciseDrawer({ programId, workstreamId, onClose }) {
    const lk = useCmLookups(programId);
    const all = useWorkstreamDetails(programId);
    const link = useSetExerciseWorkstream(programId, {
        errors: 'inline',
        success: (r)=>`Exercise linked. ${r.result?.mint?.inserted ? `${r.result.mint.inserted} dates scheduled.` : 'No dates changed.'}`
    });
    const [exerciseId, setExerciseId] = useState('');
    const [picked, setPicked] = useState('');
    const exercise = lk.exerciseById.get(exerciseId);
    const lines = exercise?.lines ?? [];
    // An exercise's one line is its line; of several, the user names one.
    const clinNumber = lines.length === 1 ? lines[0].clinNumber : picked;
    // The line's whole reviewer set: keep every other team this line already
    // names, so linking here never unlinks someone else.
    const others = new Set();
    for (const ws of all.data ?? []){
        for (const line of ws.exerciseLines){
            if (line.exerciseId !== exerciseId) continue;
            if (clinNumber && line.clinNumber !== clinNumber) continue;
            for (const w of line.workstreams)if (w.basis === 'reviewer') others.add(w.workstreamId);
        }
    }
    others.add(workstreamId);
    // Until every team's lines are read, the set above could miss one, and
    // sending it would unlink that team: Link waits for all of them.
    const allLoaded = !lk.isLoading && all.isSuccess;
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Link an exercise line",
        description: "The exercise line authorizes this team: its dates are scheduled from the line’s PoP.",
        onClose: onClose,
        busy: link.isPending,
        testId: Testids.cmWorkstream.linkExerciseDrawer,
        footerProps: {
            confirmLabel: 'Link',
            onConfirm: ()=>link.mutate({
                    exerciseId,
                    clinNumber,
                    workstreamIds: [
                        ...others
                    ]
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: !exerciseId || lines.length > 1 && !picked || !allLoaded || link.isPending,
            loading: link.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkstream.linkExerciseSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                lk.exercises.length === 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: [
                        "No exercises recorded yet. Record one on Optional Tasks first.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraLink, {
                            href: `/delivery/programs/${programId}/optional-tasks`,
                            renderLink: renderRouterLink,
                            children: "Open Optional Tasks"
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Exercise",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: exerciseId,
                        onChange: (e)=>{
                            setExerciseId(e.target.value);
                            setPicked('');
                        },
                        "data-testid": Testids.cmWorkstream.linkExercisePicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the exercise…"
                            }),
                            lk.exercises.map((e)=>/*#__PURE__*/ _jsxs("option", {
                                    value: e.id,
                                    children: [
                                        lk.optionalTaskById.get(e.optionalTaskId)?.short ?? 'OT',
                                        ' ',
                                        e.clinNumber || 'exercise',
                                        " · ",
                                        cmSpan(e.start, e.end)
                                    ]
                                }, e.id))
                        ]
                    })
                }),
                lines.length > 1 ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Line",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: picked,
                        onChange: (e)=>setPicked(e.target.value),
                        "data-testid": Testids.cmWorkstream.linkExerciseLinePicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick this team’s line…"
                            }),
                            lines.map((l)=>/*#__PURE__*/ _jsxs("option", {
                                    value: l.clinNumber,
                                    children: [
                                        l.clinNumber,
                                        l.description ? ` · ${clipLineText(l.description)}` : ''
                                    ]
                                }, l.clinNumber))
                        ]
                    })
                }) : null,
                exercise && !exercise.clinNumber && lines.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "warning",
                    children: "This exercise has no line number; the link uses the exercise’s own CLIN."
                }) : null,
                link.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(link.error, 'Could not save')
                }) : null
            ]
        })
    });
}
/** A line's printed description, on one line and short enough for a picker. */ function clipLineText(text) {
    const t = text.replace(/\s+/g, ' ').trim();
    return t.length > 80 ? `${t.slice(0, 79)}…` : t;
}
